import { type FC, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Alert } from '@interstate/components/Alert';
import { useCustomerCardSelector } from '../../hooks/useCustomerCardSelector';
import * as customerCardSelectors from '../../store/selectors';
import { selectSearchResult, clearSearchResults } from '../../store/cardOperationActionCreators';
import type { SearchResult } from '../../types/search.types';
import { getSearchResultId } from '../../utils/search';
import { SearchViewContainer } from './search.styles';
import { getSearchDescriptor } from './searchDescriptors';
import SearchBanner from './SearchBanner';
import SearchField from './SearchField';
import SearchLoading from './SearchLoading';
import SearchResultsHeader from './SearchResultsHeader';

interface SearchViewProps {
    /** Receives the typed query so the card can seed a draft for the record being created. */
    onAddNew: (typedText: string) => void;
}

const SearchView: FC<SearchViewProps> = ({ onAddNew }) => {
    const dispatch = useDispatch();
    const operationKind = useCustomerCardSelector(customerCardSelectors.getOperationKind);
    const results = useCustomerCardSelector(customerCardSelectors.getVisibleSearchResults);
    const loading = useCustomerCardSelector(customerCardSelectors.getSearchLoading);
    const error = useCustomerCardSelector(customerCardSelectors.getSearchError);
    const selected = useCustomerCardSelector(customerCardSelectors.getSelectedSearchResult);
    const searchTerm = useCustomerCardSelector(customerCardSelectors.getSearchTerm);
    // Seeded from the store so Back from the edit step returns the query alongside its results.
    const [searchValue, setSearchValue] = useState(() => searchTerm ?? '');
    const hasSearched = searchTerm !== null;

    const descriptor = getSearchDescriptor(operationKind);
    if (!descriptor) return null;

    const { testIdPrefix, RowComponent } = descriptor;
    const selectedId = selected ? getSearchResultId(selected) : null;

    const handleSearch = () => {
        if (!searchValue.trim()) return;
        dispatch(descriptor.run(searchValue) as never);
    };

    const handleClear = () => {
        setSearchValue('');
        dispatch(clearSearchResults());
    };

    const handleResultSelect = (entity: SearchResult) => {
        dispatch(selectSearchResult(entity));
    };

    return (
        <SearchViewContainer data-testid={`${testIdPrefix}-ui`}>
            <SearchBanner />

            <SearchField
                testIdPrefix={testIdPrefix}
                label={descriptor.searchLabel}
                placeholder={descriptor.placeholder}
                value={searchValue}
                onChange={setSearchValue}
                onSearch={handleSearch}
                onClear={handleClear}
            />

            {loading && <SearchLoading testId={`${testIdPrefix}-loading`} />}

            {error && (
                <Alert type="error" data-testid={`${testIdPrefix}-error`}>
                    {error}
                </Alert>
            )}

            {!loading && !error && (
                <div data-testid={`${testIdPrefix}-results`}>
                    {hasSearched && (
                        <SearchResultsHeader
                            testIdPrefix={testIdPrefix}
                            resultCount={results.length}
                            addNewLabel={descriptor.addNewLabel}
                            addNewIcon={descriptor.addNewIcon}
                            onAddNew={() => onAddNew(searchValue.trim())}
                        />
                    )}
                    {results.map((entity) => (
                        <RowComponent
                            key={getSearchResultId(entity)}
                            entity={entity}
                            isSelected={selectedId === getSearchResultId(entity)}
                            onSelect={handleResultSelect}
                        />
                    ))}
                </div>
            )}
        </SearchViewContainer>
    );
};

export default SearchView;
